import { Graphics } from 'pixi.js';
import type { EnemyType } from '../sim/entities/enemyDefs';
import { Palette } from './Palette';

/** Distinct silhouettes per enemy type — readability rule: no two alike.
 *  `bossVariant` picks the boss-pool look (BossDef.id); defaults to warden. */
export function drawEnemy(type: EnemyType, bossVariant?: string): Graphics {
  const g = new Graphics();
  if (type === 'boss') {
    drawBoss(g, bossVariant ?? 'warden');
    return g;
  }
  switch (type) {
    case 'shambler': // jagged pink blob — melee chaser
      for (const [r, a] of [[22, 0.06], [18, 0.1]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
      }
      g.poly([0, -14, 10, -6, 13, 6, 6, 13, -6, 13, -13, 6, -10, -6]).fill(Palette.enemyBody);
      g.poly([0, -14, 10, -6, 13, 6, 6, 13, -6, 13, -13, 6, -10, -6])
        .stroke({ width: 2, color: Palette.enemyHot, alpha: 0.9 });
      g.circle(-4, -2, 2).fill(Palette.enemyHot);
      g.circle(4, -2, 2).fill(Palette.enemyHot);
      break;

    case 'watcher': // orange eye triangle — ranged
      for (const [r, a] of [[21, 0.06], [17, 0.1]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHotAlt, alpha: a });
      }
      g.poly([0, -15, 13, 9, -13, 9]).fill(Palette.enemyBodyAlt);
      g.poly([0, -15, 13, 9, -13, 9]).stroke({ width: 2, color: Palette.enemyHotAlt, alpha: 0.9 });
      g.circle(0, 1, 5).fill(0x1a1020);
      g.circle(0, 1, 2.5).fill(Palette.enemyHotAlt);
      break;

    case 'husk': // tall pink shard — lunger
      for (const [r, a] of [[24, 0.06], [19, 0.1]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
      }
      g.poly([0, -18, 11, 0, 0, 18, -11, 0]).fill(Palette.enemyBody);
      g.poly([0, -18, 11, 0, 0, 18, -11, 0]).stroke({ width: 2, color: Palette.enemyHot, alpha: 0.9 });
      g.poly([0, -9, 5, 0, 0, 9, -5, 0]).fill({ color: Palette.enemyHot, alpha: 0.7 });
      break;

    case 'keeper': // vault guardian: heavy ringed circle crowned in gold
      for (const [r, a] of [[38, 0.05], [32, 0.08], [27, 0.12]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.gold, alpha: a });
      }
      g.circle(0, 0, 21).fill(0x2a2416);
      g.circle(0, 0, 21).stroke({ width: 3, color: Palette.gold, alpha: 0.95 });
      g.circle(0, 0, 13).stroke({ width: 2, color: Palette.gold, alpha: 0.55 });
      // Keyhole eye.
      g.circle(0, -3, 4).fill(Palette.gold);
      g.rect(-2, -2, 4, 9).fill(Palette.gold);
      // Crown nubs.
      g.poly([-12, -18, -8, -26, -4, -19]).fill(Palette.gold);
      g.poly([-3, -20, 0, -29, 3, -20]).fill(Palette.gold);
      g.poly([4, -19, 8, -26, 12, -18]).fill(Palette.gold);
      break;

    case 'goblin': { // treasure goblin: lumpy gold coin-sack, cinched in pink
      // Gold = loot lane (it IS loot); the squat sack silhouette + small
      // radius keep it from reading as the Keeper's crowned ringed circle.
      for (const [r, a] of [[22, 0.06], [17, 0.1]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.gold, alpha: a });
      }
      // Sack body: squat rounded blob, widest at the bottom.
      g.poly([0, -12, 8, -7, 12, 3, 9, 11, -9, 11, -12, 3, -8, -7]).fill(0x4a3a16);
      g.poly([0, -12, 8, -7, 12, 3, 9, 11, -9, 11, -12, 3, -8, -7])
        .stroke({ width: 2, color: Palette.gold, alpha: 0.9 });
      // Cinch knot in the melee-family pink so it still reads "enemy".
      g.circle(0, -12, 3).fill(Palette.enemyHot);
      // Gems straining through the burlap.
      g.circle(-4, 3, 2).fill(Palette.gold);
      g.circle(4, 1, 2).fill(Palette.gold);
      g.circle(1, 7, 2).fill(Palette.gold);
      // Nervous little feet.
      g.rect(-6, 11, 4, 3).fill(Palette.enemyHot);
      g.rect(2, 11, 4, 3).fill(Palette.enemyHot);
      break;
    }

    case 'chanter': // orange ringed circle — caster
      for (const [r, a] of [[23, 0.06], [19, 0.1]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHotAlt, alpha: a });
      }
      g.circle(0, 0, 14).fill(Palette.enemyBodyAlt);
      g.circle(0, 0, 14).stroke({ width: 2, color: Palette.enemyHotAlt, alpha: 0.9 });
      g.circle(0, 0, 8).stroke({ width: 2, color: Palette.enemyHotAlt, alpha: 0.6 });
      g.circle(0, 0, 3).fill(0xffffff);
      break;

    case 'splitter': // lumpy pink 3-lobe blob — the mites-within show through
      for (const [r, a] of [[25, 0.06], [21, 0.1]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
      }
      g.circle(-6, 3, 11).fill(Palette.enemyBody);
      g.circle(7, 4, 9).fill(Palette.enemyBody);
      g.circle(1, -7, 8).fill(Palette.enemyBody);
      g.circle(-6, 3, 11).stroke({ width: 2, color: Palette.enemyHot, alpha: 0.9 });
      g.circle(7, 4, 9).stroke({ width: 2, color: Palette.enemyHot, alpha: 0.9 });
      g.circle(1, -7, 8).stroke({ width: 2, color: Palette.enemyHot, alpha: 0.9 });
      // Dim inner mites — the death-burst tell.
      g.circle(-5, 2, 3).fill({ color: Palette.enemyHot, alpha: 0.55 });
      g.circle(6, 3, 3).fill({ color: Palette.enemyHot, alpha: 0.55 });
      break;

    case 'mite': // tiny pink wedge — smallness IS the silhouette
      g.circle(0, 0, 12).fill({ color: Palette.enemyHot, alpha: 0.08 });
      g.poly([0, -8, 7, 6, -7, 6]).fill(Palette.enemyBody);
      g.poly([0, -8, 7, 6, -7, 6]).stroke({ width: 1.5, color: Palette.enemyHot, alpha: 0.9 });
      break;

    case 'brute': // wide flat pink slab — the only enemy broader than tall
      for (const [r, a] of [[30, 0.06], [25, 0.1]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
      }
      g.poly([-26, 0, -16, -16, 16, -16, 26, 0, 16, 16, -16, 16])
        .fill(Palette.enemyBody);
      g.poly([-26, 0, -16, -16, 16, -16, 26, 0, 16, 16, -16, 16])
        .stroke({ width: 3, color: Palette.enemyHot, alpha: 0.9 });
      // Knuckle studs, low.
      g.circle(-10, 9, 2.5).fill(Palette.enemyHot);
      g.circle(10, 9, 2.5).fill(Palette.enemyHot);
      break;

    case 'flanker': // slim orange concave dart — reads "fast"
      g.circle(0, 0, 20).fill({ color: Palette.enemyHotAlt, alpha: 0.07 });
      g.poly([0, -16, 8, 10, 0, 4, -8, 10]).fill(Palette.enemyBodyAlt);
      g.poly([0, -16, 8, 10, 0, 4, -8, 10])
        .stroke({ width: 2, color: Palette.enemyHotAlt, alpha: 0.9 });
      g.circle(0, -6, 2).fill(Palette.enemyHotAlt);
      break;

    case 'leech': // orange open crescent + proboscis ticks — the healer
      g.circle(0, 0, 18).fill({ color: Palette.enemyHotAlt, alpha: 0.07 });
      g.arc(0, 0, 10, Math.PI * 0.35, Math.PI * 1.85)
        .stroke({ width: 4, color: Palette.enemyBodyAlt });
      g.arc(0, 0, 10, Math.PI * 0.35, Math.PI * 1.85)
        .stroke({ width: 1.5, color: Palette.enemyHotAlt, alpha: 0.8 });
      g.circle(7, -5, 1.6).fill(Palette.enemyHotAlt);
      g.circle(9, 0, 1.6).fill(Palette.enemyHotAlt);
      g.circle(7, 5, 1.6).fill(Palette.enemyHotAlt);
      break;
  }
  return g;
}

/** Boss-pool visuals: same hot-lane language (pink/orange, white cores),
 *  one unmistakable silhouette per pool boss. Authored to each def radius. */
function drawBoss(g: Graphics, variant: string): void {
  switch (variant) {
    case 'horror': // swarm-lord (r28): core diamond ringed by orbiting young
      for (const [r, a] of [[44, 0.05], [36, 0.09], [30, 0.13]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
      }
      g.poly([0, -26, 20, 0, 0, 26, -20, 0]).fill(0x3a1526);
      g.poly([0, -26, 20, 0, 0, 26, -20, 0]).stroke({ width: 3, color: Palette.enemyHot });
      // Orbiting spawn-diamonds — the brood tell.
      for (const [ox, oy] of [[0, -36], [31, 18], [-31, 18]] as const) {
        g.poly([ox, oy - 8, ox + 6, oy, ox, oy + 8, ox - 6, oy]).fill(Palette.enemyBody);
        g.poly([ox, oy - 8, ox + 6, oy, ox, oy + 8, ox - 6, oy])
          .stroke({ width: 1.5, color: Palette.enemyHot, alpha: 0.9 });
      }
      // Scattered mouth dots.
      g.circle(-6, -4, 2).fill(Palette.enemyHot);
      g.circle(6, -2, 2).fill(Palette.enemyHot);
      g.circle(0, 8, 2).fill(Palette.enemyHot);
      g.circle(0, -2, 4).fill(0xffffff);
      break;

    case 'bellkeeper': // tanky ring-caster (r42): a great orange bell
      for (const [r, a] of [[60, 0.05], [52, 0.08], [46, 0.12]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHotAlt, alpha: a });
      }
      g.circle(0, 0, 40).fill(0x33200f);
      g.circle(0, 0, 40).stroke({ width: 3.5, color: Palette.enemyHotAlt });
      g.circle(0, 0, 30).stroke({ width: 2, color: Palette.enemyHotAlt, alpha: 0.55 });
      g.circle(0, 0, 20).stroke({ width: 2, color: Palette.enemyHotAlt, alpha: 0.35 });
      // Hanging clapper.
      g.rect(-2.5, 8, 5, 20).fill(Palette.enemyBodyAlt);
      g.circle(0, 30, 6).fill(Palette.enemyHotAlt);
      g.circle(0, -6, 5).fill(0xffffff);
      g.circle(0, -6, 8).stroke({ width: 1.5, color: 0xffffff, alpha: 0.5 });
      break;

    case 'esoterium': // duelist (r26): tall narrow blade, reads FAST
      for (const [r, a] of [[42, 0.05], [34, 0.09], [28, 0.13]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
      }
      g.poly([0, -36, 12, -6, 8, 30, -8, 30, -12, -6]).fill(0x3a1526);
      g.poly([0, -36, 12, -6, 8, 30, -8, 30, -12, -6])
        .stroke({ width: 3, color: Palette.enemyHot });
      // White core stripe — the lance within.
      g.poly([0, -28, 3, -4, 0, 24, -3, -4]).fill({ color: 0xffffff, alpha: 0.85 });
      // Winglet barbs.
      g.poly([12, -8, 24, -14, 14, 2]).fill({ color: Palette.enemyHot, alpha: 0.6 });
      g.poly([-12, -8, -24, -14, -14, 2]).fill({ color: Palette.enemyHot, alpha: 0.6 });
      break;

    case 'gravemother': // zoner (r38): wide hexagon slab, budding young zones
      for (const [r, a] of [[56, 0.05], [48, 0.08], [42, 0.12]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHotAlt, alpha: a });
      }
      g.poly([-38, 0, -20, -26, 20, -26, 38, 0, 20, 26, -20, 26]).fill(0x2c1530);
      g.poly([-38, 0, -20, -26, 20, -26, 38, 0, 20, 26, -20, 26])
        .stroke({ width: 3, color: Palette.enemyHotAlt });
      // Dim inner buds — splitter family language for the blooming zones.
      g.circle(-14, 4, 8).fill({ color: Palette.enemyHotAlt, alpha: 0.4 });
      g.circle(13, 6, 7).fill({ color: Palette.enemyHotAlt, alpha: 0.4 });
      g.circle(0, -10, 7).fill({ color: Palette.enemyHotAlt, alpha: 0.4 });
      g.circle(0, 2, 4).fill(0xffffff);
      break;

    default: // 'warden' — the Pale Warden: crowned twin-diamond colossus (r34)
      for (const [r, a] of [[52, 0.05], [44, 0.08], [38, 0.12]] as const) {
        g.circle(0, 0, r).fill({ color: Palette.enemyHot, alpha: a });
      }
      g.poly([0, -34, 26, 0, 0, 34, -26, 0]).fill(0x3a1526);
      g.poly([0, -34, 26, 0, 0, 34, -26, 0]).stroke({ width: 3, color: Palette.enemyHot });
      g.poly([0, -20, 15, 0, 0, 20, -15, 0]).fill({ color: Palette.enemyHot, alpha: 0.55 });
      // Crown spikes.
      g.poly([-14, -26, -8, -38, -4, -27]).fill(Palette.enemyHotAlt);
      g.poly([-4, -29, 0, -44, 4, -29]).fill(Palette.enemyHotAlt);
      g.poly([4, -27, 8, -38, 14, -26]).fill(Palette.enemyHotAlt);
      g.circle(0, -2, 5).fill(0xffffff);
      g.circle(0, -2, 8).stroke({ width: 1.5, color: 0xffffff, alpha: 0.5 });
      break;
  }
}
